import { Injectable } from '@angular/core';
import {
  IAnalyticsOptions,
  ITrackingAction,
  ITrackingObject,
  ITrackingProperties,
} from './models';
import { I2gNgTrackingBuilder } from './i2g-ng-tracking.builder';

const md5 = require('md5');

@Injectable({
  providedIn: 'root',
})
export class I2gNgTrackingService {
  private _loadedAnalytics: boolean;
  private _options: IAnalyticsOptions;
  private _traits: ITrackingProperties;
  private _window: any;

  constructor(private _trackingBuilder: I2gNgTrackingBuilder) {
    this._window = this.getNativeWindow();
  }

  /**
   * Initialize the Client
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#load-options
   * @param segmentWriteKey Segment write key
   * @param options Analytics.js options
   */
  load(segmentWriteKey: string, options: IAnalyticsOptions) {
    if (!this._window.hasOwnProperty('analytics')) {
      throw new Error('Analytics JS is missing');
    }

    if (!this._loadedAnalytics) {
      this._window.analytics.load(segmentWriteKey);

      this._loadedAnalytics = true;
      this._options = options;
    }
  }

  /**
   * Generate unique id for identify calls.
   *
   * Currently, the Segment userId field, which is supposed to be a unique identifier,
   * contains the user’s email address. We want to stop using the user’s email address in this field, and instead
   * populate the field with the result of an MD5 hash of the following two i2g fields: accountuser.userid and
   * accountuser.accountid. The hash should have a dash delimiter to avoid collisions: [hash of userid]-[hash of
   * accountid]
   *
   * @param userId User ID
   * @param accountId Account ID
   */
  generateUniqueId(userId: string, accountId: string): string {
    if (!userId || !accountId) {
      throw new Error('User ID and Account ID are required');
    }

    return `${md5(userId)}-${md5(accountId)}`;
  }

  /**
   * The identify method is how you associate your users and their actions to a recognizable userId and traits.
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
   * @param userId The database ID for the user. If you don’t know who the user is yet, you can omit the userId and
   *   just record traits.
   * @param traits A dictionary of traits you know about the user, like their email or name.
   */
  identify(userId: string, traits?: ITrackingProperties): void {
    if (traits) {
      this._traits = traits;
    }
    this._window.analytics.identify(
      userId,
      traits,
      this._trackingBuilder.buildContext(this._options, traits)
    );
  }

  /**
   * The page method lets you record page views on your website, along with optional extra information about the page
   * being viewed. Because some destinations require a page call to instantiate their libraries, you must call page at
   * least once per page load! You may call it more than once if needed, (eg, on virtual page changes in a single page
   * app).
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
   * @param pageName The name of the page.
   * @param properties A dictionary of properties of the page. Note: url, title, referrer and path are collected
   *   automatically!
   */
  trackPage(pageName: string, properties: ITrackingProperties = null): void {
    const category = null;

    this._window.analytics.page(
      category,
      pageName,
      properties,
      this._trackingBuilder.buildContext(this._options, this._traits)
    );
  }

  /**
   * The track method lets you record any actions your users perform.
   *
   * We use a combination of tracking object and action to construct the final tracking event.
   * For example if we want to track a button click inside a document, the tracking object would be
   * the document and the tracking action would be button tapped. The final event name will be a
   * combination of object and action name, the event properties would be combination of properties from
   * both tracking object and tracking action.
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
   * @param trackingObjects A dictionary of properties for identifying an entity associated with the event.
   * @param trackingAction A dictionary of properties for identifying an action associated with the event.
   * @param additionalProperties A dictionary of additional properties for an event.
   */
  trackEvent(
    trackingObjects: ITrackingObject | ITrackingObject[],
    trackingAction: ITrackingAction,
    additionalProperties?: ITrackingProperties
  ): void {
    if (trackingObjects && trackingAction) {
      const trackingEvent = this._trackingBuilder.buildTrackingEvent(
        trackingObjects,
        trackingAction,
        additionalProperties
      );
      const event = trackingEvent.name;
      const properties = trackingEvent.properties;

      this._window.analytics.track(
        event,
        properties,
        this._trackingBuilder.buildContext(this._options, this._traits)
      );
    }
  }

  private getNativeWindow(): any {
    return window;
  }
}
